<script lang="ts" setup>
    import type { SearchResult } from '@/types/sammy/response';
    import type { Filter, FilterOption } from '@/types/filters';
    import { buildPreviewLink } from '@/utils/buildPreviewLink';

    import { convertAggregationsToFilters, convertFiltersToQuery, restoreSelectedFilters } from '@/utils/filters';

    const props = defineProps<{ searchResult: SearchResult }>();

    const { lang, urlSegment } = useLanguage();
    const { t } = useI18n();
    const { getDownloadName } = useGetDownloadName();

    const route = useRoute();
    const router = useRouter();

    const query = computed(() => route.query);

    const pending = ref(false);
    const filters = ref<Filter[]>([]);

    const order = ref(query.value.order || 'relevancy');
    const selectedFilters = ref<Record<string, FilterOption>>({});
    const selectedFiltersParams = computed(() => convertFiltersToQuery(selectedFilters));

    const totalResultsCount = computed(() => props.searchResult?.aggregations?.unique_documents_unfiltered?.value);
    const filteredResultsCount = computed(
        () => props.searchResult?.aggregations?.filtered_aggs?.unique_documents_filtered.value
    );

    if (props.searchResult?.aggregations?.filtered_aggs) {
        const newFilters = convertAggregationsToFilters(props.searchResult?.aggregations.filtered_aggs, t);

        filters.value = newFilters;
    }

    watch([order, selectedFiltersParams], ([newOrder, newSelectedFiltersParams]) => {
        router.replace({
            query: {
                order: newOrder,
                q: route.query.q,
                ...newSelectedFiltersParams
            }
        });
    });

    watch(
        query,
        async (newQuery) => {
            pending.value = true;

            if (props.searchResult.aggregations) {
                const newFilters = convertAggregationsToFilters(props.searchResult.aggregations.filtered_aggs, t);

                const objects = getObjectsFromQuery(newQuery as Record<string, string>);
                const { filters: f, selectedFilters: s } = restoreSelectedFilters(newFilters, objects.filters);
                filters.value = f;
                selectedFilters.value = s;
            }
            pending.value = false;
        },
        { immediate: true }
    );

    function updateFilter(type: string, newValue: FilterOption) {
        if (newValue.key === 'all') {
            Reflect.deleteProperty(selectedFilters.value, type);
        } else {
            selectedFilters.value[type] = newValue;
        }
    }

    function resetFilters() {
        selectedFilters.value = {};
        filters.value = [];
    }

    function removeSelectedFilter(type: string) {
        Reflect.deleteProperty(selectedFilters.value, type);
        filters.value = [];
    }
</script>

<template>
    <div class="container">
        <SidebarGrid
            :title="t('search.results')"
            :sidebar-title="t('pagePlp.sidebar.title')"
            :sidebar-button-open-text="t('pagePlp.sidebar.buttonOpenText')"
            :sidebar-button-closed-text="t('pagePlp.sidebar.buttonClosedText')"
            :total-found-entries="totalResultsCount"
            @reset-filters="resetFilters"
        >
            <template #sidebar>
                <template
                    v-for="filter in filters"
                    :key="filter.type"
                >
                    <component
                        :is="filter.component"
                        v-if="filter.props.visible"
                        v-bind="filter.props"
                        @update:selected-option="($event: FilterOption) => updateFilter(filter.type, $event)"
                        @update:is-active="($event: FilterOption) => updateFilter(filter.type, $event)"
                    />
                </template>
            </template>

            <template #user-actions>
                <div class="select-element">
                    <label for="sort-select">{{ t('pagePlp.sort.sortBy') }}</label>
                    <select
                        id="sort-select"
                        v-model="order"
                        :aria-label="t('pagePlp.sort.option1')"
                    >
                        <option value="relevancy">{{ t('pagePlp.sort.option0') }}</option>
                        <option value="asc">{{ t('pagePlp.sort.option1') }}</option>
                        <option value="desc">{{ t('pagePlp.sort.option2') }}</option>
                    </select>
                </div>
            </template>

            <template #filter-chips>
                <FilterChips
                    :selected-filters="selectedFilters"
                    :total-entries="totalResultsCount"
                    :filtered-entries="filteredResultsCount"
                    product-count-translation="pagePlp.searchResultCount"
                    @remove="removeSelectedFilter"
                />
            </template>

            <template #content>
                <ul
                    class="search-grid"
                    data-testid="fachportal-search-results"
                >
                    <li
                        v-for="item in searchResult?.hits?.hits"
                        :key="item._id"
                    >
                        <FachkreisSearchCard
                            :search-type="item._source.storyblok_section || item._index"
                            :product-prescription-only="item._source.medice_product_prescription_only"
                            :product-indications="item._source.medice_production_indication"
                            :product-applications="item._source.medice_product_application_area"
                            :document-type="getDownloadName(item._source.asset_type)"
                            :document-total-chunks="(item._source as AggregatedDocument).totalChunks"
                            :document-chunks="(item._source as AggregatedDocument).__chunk_texts"
                            :image-id="item._source.medice_main_picture_id || null"
                            :title="
                                item._source.censhare_name?.[lang] ||
                                item._source.censhare_name?.default ||
                                item._source.storyblok_title?.[lang]?.[0] ||
                                item._source.storyblok_title?.default?.[0]
                            "
                            :link-to="buildPreviewLink(item, urlSegment, lang!)"
                            :highlight="item.highlight?.[`storyblok_body.${lang}`]"
                            :date="item._source?.storyblok_date"
                        />
                    </li>
                </ul>
            </template>
        </SidebarGrid>
    </div>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/pages/utils/shared-components.scss';
    @import '@/assets/scss/fachkreis/fachkreis-search.scss';
</style>
